import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  CircularProgress,
  TablePagination,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import SearchIcon from '@mui/icons-material/Search';
import {
  allPointOfInterestTemplates as allPointOfInterestTemplatesState,
  unitSystem as unitSystemState,
} from '@/components/Analysis/state';
import { Can } from '@/components/Can';
import { POIDialogTab } from './Summary/style';
import { abyssColors } from '@/theme/colors';
import {
  Assembly,
  GetAssembliesByTemplateFieldsFiltersForFilterSideBarQuery,
  PoiStatus,
  PointOfInterestsDocument,
  PointOfInterestsQuery,
  StatusRemediatedThrough,
  useGetAssembliesByPaintRegionIdsQuery,
  usePointOfInterestsQuery,
  useUpdatePointOfInterestsStatusMutation,
  useUpdateAssemblyRemediationMutation,
  useGetAssemblyPaintRegionsByPaintRegionIdQuery,
  StructureDeploymentLevel,
  useGetStructureDeploymentLevelQuery,
  useAddPartialRemediationMutation,
  PaintRegionStatus,
  GetAssemblyPaintRegionsByPaintRegionIdDocument,
  AllPaintRegionsForPaintBlockTableDocument,
  RiskRemediationTrendAnalyticsDocument,
  UnitSystemEnum,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { usePaintRegionStatusLabels } from '@/hooks';
import { upperSnakeCase } from '@/utils';
import { scrollDesign } from '../styles';
import { PageLoader } from '@/components/shared/PageLoader';
import { CustomTableBody } from '@/components/Analysis/shared/EquipmentTable/CustomTableBody';
import { CustomTableHead } from '@/components/Analysis/shared/EquipmentTable/CustomTableHead';
import {
  TablePaginationContainer,
  StyledTableContainer,
  ScrollableBox,
  StyledTable,
  PaginationBox,
} from '@/components/Analysis/Insights/EquipmentTable/styles';
import { ALL_POIS_TAB_LABEL } from '@/constants';
import usePoiData, { RowData } from '../PointOfInterests/usePoiData';

import { poiState } from '@/components/Analysis/modules/pointOfInterest';
import { usePoiColumns } from '@/hooks/usePoiColumns';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks';
import { CustomTable } from '@/components/Analysis/shared/CustomTable/CustomTable';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import * as Sentry from '@sentry/react';
import { convertSquareFeetToSquareMeters, convertSqMetersToSqFeet } from '@/utils/unitSystem';

type PoiDialogProps = {
  isOpen: boolean;
  toggleDialog: (status: boolean) => void;
  paintRegionId: string;
  remediationDate?: string | null;
};

type SelectedPois = {
  [key: string]: string[];
};

type SelectedAssemblies = {
  [key: string]: string[];
};

type MainTabType = 'poi' | 'assembly' | 'areaRisk';

type AssemblyData = NonNullable<
  GetAssembliesByTemplateFieldsFiltersForFilterSideBarQuery['getAssembliesByTemplateFieldsFilters']
>[0];

const getDialogTitle = (mainTab: MainTabType, completedLabel: string): string => {
  const label = completedLabel.toLowerCase();
  switch (mainTab) {
    case 'poi':
      return `Select POIs to mark as ${label}`;
    case 'assembly':
      return `Select Assemblies to mark as ${label}`;
    default:
      return `Add Area to mark as ${label} in this block`;
  }
};

export const PoiDialogBox = ({
  isOpen,
  toggleDialog,
  paintRegionId,
  remediationDate,
}: PoiDialogProps) => {
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const allPointOfInterestTemplates = useAtomValue(allPointOfInterestTemplatesState);
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();
  const { getPaintBlockLabels } = usePaintRegionStatusLabels();
  const completedLabel = getPaintBlockLabels(PaintRegionStatus.Completed);
  const [selectedPOIType, setSelectedPOIType] = useState<string | undefined | null>('');
  const [unfinishedSearchTerm, setUnfinishedSearchTerm] = useState('');
  const [pointOfInterests, setPointOfInterests] = useState<
    PointOfInterestsQuery['pointOfInterests']
  >([]);
  const [assemblies, setAssemblies] = useState<AssemblyData[]>([]);
  const [completePointOfInterest] = useUpdatePointOfInterestsStatusMutation();
  const structureId = useGetStructureId();

  // Fetch structure deployment level to conditionally show Coating area tab
  const { data: structureDeploymentLevelData } = useGetStructureDeploymentLevelQuery({
    variables: { structureId: structureId || '' },
    skip: !structureId || !isOpen,
  });

  const deploymentLevel = structureDeploymentLevelData?.structure?.deploymentLevel;
  const showCoatingAreaTab = deploymentLevel === StructureDeploymentLevel.FacilityLevel;

  const [updateAssemblyCompletion] = useUpdateAssemblyRemediationMutation();

  const [selectedPointOfInterestId, setSelectedPointOfInterestId] = useAtom(
    poiState.selectedPointOfInterestId
  );
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const allPOIsTemplatesWithAllTab = useMemo(
    () => [
      { name: ALL_POIS_TAB_LABEL, type: '', fields: [], key: 'all' },
      ...allPointOfInterestTemplates,
    ],
    [allPointOfInterestTemplates]
  );
  const [page, setPage] = useState(0);
  const [poiTotalCount, setPoiTotalCount] = useState(0);
  const selectedPoiTemplate = allPOIsTemplatesWithAllTab[0];
  const [selectedPois, setSelectedPois] = useState<SelectedPois>({});
  const [selectedAssemblies, setSelectedAssemblies] = useState<SelectedAssemblies>({});
  const [sortDirection, setSortDirection] = useState<number>(1);
  const [sortBy, setSortBy] = useState('createdAt');
  const [mainTab, setMainTab] = useState<MainTabType>('poi');
  const [selectedAssemblyGroup, setSelectedAssemblyGroup] = useState<string>('All');
  // Debounced search term
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  // Area Risk state
  const [assemblyPaintRegionIds, setAssemblyPaintRegionIds] = useState<string[]>([]);
  const [savingCompletion, setSavingCompletion] = useState(false);
  const [totalCompletedArea, setTotalCompletedArea] = useState<number>(0);
  const unitSystem = useAtomValue(unitSystemState);

  const [addPartialCompletion] = useAddPartialRemediationMutation();

  // Fetch assembly paint regions for the selected paint region
  const { data: assemblyPaintRegionsData, loading: assemblyPaintRegionsLoading } =
    useGetAssemblyPaintRegionsByPaintRegionIdQuery({
      variables: {
        input: {
          structureIds: [structureId],
          paintRegionIds: [paintRegionId],
        },
      },
      skip: !isOpen || mainTab !== 'areaRisk' || !paintRegionId,
      fetchPolicy: 'network-only',
    });

  const areaRiskLoading = assemblyPaintRegionsLoading;

  // Switch away from areaRisk tab if it becomes unavailable
  useEffect(() => {
    if (mainTab === 'areaRisk' && !showCoatingAreaTab) {
      setMainTab('poi');
    }
  }, [mainTab, showCoatingAreaTab]);

  // Switch away from assembly tab if it becomes unavailable (when deploymentLevel is FacilityLevel)
  useEffect(() => {
    if (mainTab === 'assembly' && deploymentLevel === StructureDeploymentLevel.FacilityLevel) {
      setMainTab('poi');
    }
  }, [mainTab, deploymentLevel]);

  // For Area Risk: Only use the FIRST assembly paint region for this paint region
  // This ensures we're completing a single paint block, not all blocks in the region
  useEffect(() => {
    if (mainTab === 'areaRisk' && assemblyPaintRegionsData?.allAssemblyPaintRegions) {
      const assemblyPaintRegions = assemblyPaintRegionsData.allAssemblyPaintRegions;

      // Use all assembly paint regions (multiple paint blocks)
      if (assemblyPaintRegions.length > 0) {
        setAssemblyPaintRegionIds(assemblyPaintRegions.map((region) => region.id));
      } else {
        setAssemblyPaintRegionIds([]);
      }
    } else if (mainTab !== 'areaRisk') {
      // Reset when switching away from areaRisk tab
      setAssemblyPaintRegionIds([]);
      setTotalCompletedArea(0);
    }
  }, [mainTab, assemblyPaintRegionsData]);

  // Calculate totals for all assembly paint regions
  const { totalArea, totalAlreadyCompleted, remainingArea } = useMemo(() => {
    if (!assemblyPaintRegionsData?.allAssemblyPaintRegions) {
      return { totalArea: 0, totalAlreadyCompleted: 0, remainingArea: 0 };
    }

    const riTypes = ['ri0', 'ri1', 'ri2', 'ri3', 'ri4', 'ri5'];
    let total = 0;
    let completed = 0;

    // Sum across all assembly paint regions
    for (const region of assemblyPaintRegionsData.allAssemblyPaintRegions) {
      for (const riType of riTypes) {
        const area = region.templateFieldData?.[riType];
        if (typeof area === 'number' && area > 0) {
          total += area;
        }

        const completedArea = region.remediationByRi?.[riType];
        if (typeof completedArea === 'number' && completedArea > 0) {
          completed += completedArea;
        }
      }
    }

    return {
      totalArea: total,
      totalAlreadyCompleted: completed,
      remainingArea: Math.max(0, total - completed),
    };
  }, [assemblyPaintRegionsData]);

  // Calculate cumulative completion percentage = (already completed + new) / totalArea
  const completionPercentage = useMemo(() => {
    if (totalArea > 0) {
      // Convert totalCompletedArea to square meters if needed for accurate calculation
      const completedAreaInMeters =
        unitSystem === UnitSystemEnum.Imperial
          ? convertSquareFeetToSquareMeters(totalCompletedArea || 0)
          : totalCompletedArea || 0;

      const cumulative = (totalAlreadyCompleted + completedAreaInMeters) / totalArea;
      return Math.max(0, Math.min(1, cumulative)) * 100;
    }
    return 0;
  }, [totalAlreadyCompleted, totalCompletedArea, totalArea, unitSystem]);

  // Convert remaining area for display in user's unit system
  const displayRemainingArea = useMemo(() => {
    return unitSystem === UnitSystemEnum.Imperial
      ? convertSqMetersToSqFeet(remainingArea)
      : remainingArea;
  }, [remainingArea, unitSystem]);

  // Convert total area for display in user's unit system
  const displayTotalArea = useMemo(() => {
    return unitSystem === UnitSystemEnum.Imperial ? convertSqMetersToSqFeet(totalArea) : totalArea;
  }, [totalArea, unitSystem]);

  // Convert already completed area for display in user's unit system
  const displayAlreadyCompleted = useMemo(() => {
    return unitSystem === UnitSystemEnum.Imperial
      ? convertSqMetersToSqFeet(totalAlreadyCompleted)
      : totalAlreadyCompleted;
  }, [totalAlreadyCompleted, unitSystem]);

  // Get unit label based on unit system
  const areaUnitLabel = unitSystem === UnitSystemEnum.Imperial ? 'ft²' : 'm²';

  const handleSaveAreaRiskCompletion = useCallback(async () => {
    if (!assemblyPaintRegionIds.length || totalCompletedArea <= 0) {
      return;
    }

    // Convert to square meters for saving (backend expects meters)
    // Send the entered value (may exceed remaining area) - backend will store it and cap for calculations
    const enteredArea =
      unitSystem === UnitSystemEnum.Imperial
        ? convertSquareFeetToSquareMeters(totalCompletedArea)
        : totalCompletedArea;

    setSavingCompletion(true);
    try {
      await addPartialCompletion({
        variables: {
          input: {
            assemblyPaintRegionIds,
            totalRemediatedArea: enteredArea,
            status: PaintRegionStatus.PartiallyCompleted,
            lastModifiedFor: 'Update Partial Completion',
            remediationAt: remediationDate,
          },
        },
        refetchQueries: [
          GetAssemblyPaintRegionsByPaintRegionIdDocument,
          AllPaintRegionsForPaintBlockTableDocument,
          RiskRemediationTrendAnalyticsDocument,
        ],
      });

      // Reset after save
      setTotalCompletedArea(0);
      showSnackBar('Partial completion saved successfully', 'success');
      toggleDialog(false);
    } catch (error: unknown) {
      // Extract error message from GraphQL error
      const errorMessage =
        (error as { graphQLErrors?: Array<{ message?: string }> })?.graphQLErrors?.[0]?.message ||
        (error as { message?: string })?.message ||
        (error as { originalError?: { message?: string } })?.originalError?.message ||
        'Failed to save completion';

      Sentry.captureException(error, {
        extra: {
          assemblyPaintRegionIds,
          totalCompletedArea,
          errorMessage,
        },
      });

      showSnackBar(`Error: ${errorMessage}`, 'error');
    } finally {
      setSavingCompletion(false);
    }
  }, [
    assemblyPaintRegionIds,
    totalCompletedArea,
    unitSystem,
    addPartialCompletion,
    toggleDialog,
    showSnackBar,
    remediationDate,
  ]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchTerm(unfinishedSearchTerm);
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [unfinishedSearchTerm]);

  const handleSearchTermChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUnfinishedSearchTerm(event.target.value);
    },
    [setUnfinishedSearchTerm]
  );

  const type = upperSnakeCase(selectedPOIType);

  useEffect(() => {
    setSelectedPOIType(allPointOfInterestTemplates[0]?.key);
    setSelectedPois({});
    setSelectedAssemblies({});
  }, [allPointOfInterestTemplates]);

  // Reset pagination when switching tabs, groups, or searching
  useEffect(() => {
    setPage(0);
    // Only reset rowsPerPage when switching main tabs
    if (mainTab) {
      setRowsPerPage(10);
    }
  }, [mainTab, selectedAssemblyGroup, searchTerm]);

  const eligiblePoiStatuses = useMemo(
    () => [PoiStatus.Inprogress, PoiStatus.Inreview, PoiStatus.ChaseItemAdded],
    []
  );

  const updatePoiState = useCallback((data: PointOfInterestsQuery) => {
    if (data?.pointOfInterests && data?.pointOfInterestsCount?.count !== undefined) {
      setPointOfInterests(data.pointOfInterests);
      setPoiTotalCount(data.pointOfInterestsCount.count ?? 0);
    }
  }, []);

  const { loading: poisLoading, refetch } = usePointOfInterestsQuery({
    variables: {
      input: {
        structureId,
        assemblyTemplateId: selectedAssemblyTemplateId,
        type,
        paintRegionIds: [paintRegionId],
        status: eligiblePoiStatuses,
      },
      pageInfo: {
        sortBy,
        sortDir: sortDirection,
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      },
    },
    skip: paintRegionId === '' || mainTab !== 'poi' || !isOpen,
    fetchPolicy: 'network-only',
    onCompleted(data) {
      updatePoiState(data);
    },
  });

  // Refetch POI data when dialog opens and POI tab is active
  useEffect(() => {
    if (isOpen && mainTab === 'poi' && paintRegionId) {
      refetch();
    }
  }, [isOpen, mainTab, paintRegionId, refetch]);

  const { loading: assembliesLoading, refetch: refetchAssemblies } =
    useGetAssembliesByPaintRegionIdsQuery({
      variables: {
        input: {
          structureId,
          selectedPaintRegionIds: paintRegionId ? [paintRegionId] : [],
          templateId: selectedAssemblyTemplateId ?? '',
        },
      },
      skip: !paintRegionId || mainTab !== 'assembly' || !isOpen,
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const assembliesData = data.getAssembliesByPaintRegionIds || [];
        setAssemblies(assembliesData);
      },
    });

  // Refetch assembly data when dialog opens and assembly tab is active
  useEffect(() => {
    if (isOpen && mainTab === 'assembly' && paintRegionId) {
      refetchAssemblies();
    }
  }, [isOpen, mainTab, paintRegionId, refetchAssemblies]);

  const poiColumns = usePoiColumns({
    selectedPoiTemplate,
    type,
  });
  const { rows } = usePoiData({
    pointOfInterests,
    columns: poiColumns,
    hiddenColumns: [],
  });

  const handlePoiSelected = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, poi: RowData) => {
      if ((event.target as HTMLInputElement).type === 'checkbox') {
        return;
      }
      setSelectedPointOfInterestId(
        poi.id.toString() === selectedPointOfInterestId ? undefined : poi.id.toString()
      );
    },
    [selectedPointOfInterestId, setSelectedPointOfInterestId]
  );

  const handlePointOfInterestSelect = useCallback(
    (selectedPointOfInterestIds: string[]) => {
      if (!selectedPOIType) return;
      setSelectedPois((previousSelected) => {
        const newState = { ...previousSelected };

        if (!newState[selectedPOIType]) {
          newState[selectedPOIType] = [];
        }

        newState[selectedPOIType] = selectedPointOfInterestIds;
        return newState;
      });
    },
    [selectedPOIType]
  );

  const handleAssemblySelect = useCallback(
    (selectedAssemblyIds: string[]) => {
      setSelectedAssemblies((previousSelected) => {
        const newState = { ...previousSelected };

        if (!newState[selectedAssemblyGroup]) {
          newState[selectedAssemblyGroup] = [];
        }

        const currentSelections = newState[selectedAssemblyGroup];
        const assemblyId = selectedAssemblyIds[0]; // Since we're passing single ID from checkbox

        // Toggle selection: add if not present, remove if present
        if (currentSelections.includes(assemblyId)) {
          newState[selectedAssemblyGroup] = currentSelections.filter((id) => id !== assemblyId);
        } else {
          newState[selectedAssemblyGroup] = [...currentSelections, assemblyId];
        }

        return newState;
      });
    },
    [selectedAssemblyGroup]
  );

  const getTotalPoiCount = useMemo(() => {
    return Object.values(selectedPois).reduce((total, items) => total + (items?.length ?? 0), 0);
  }, [selectedPois]);

  const getTotalAssemblyCount = useMemo(() => {
    return Object.values(selectedAssemblies).reduce(
      (total, items) => total + (items?.length ?? 0),
      0
    );
  }, [selectedAssemblies]);

  const getTotalSelectedCount = useMemo(() => {
    return getTotalPoiCount + getTotalAssemblyCount;
  }, [getTotalPoiCount, getTotalAssemblyCount]);

  const assemblyColumns = useMemo(() => {
    const createColumn = (
      key: string,
      columnName: string,
      templateFieldKey: string,
      isTemplateField = false
    ) => ({
      key: isTemplateField ? `templateFieldData.${templateFieldKey}` : key,
      columnName,
      isSortable: true,
      visibility: true,
      templateFieldKey,
      ...(isTemplateField && { isTemplateField: true }),
    });

    return [
      createColumn('tagName', 'Tag Name', 'tagName'),
      createColumn('templateFieldData.circuit', 'Circuit', 'circuit', true),
      createColumn('templateFieldData.service', 'Service', 'service', true),
      createColumn('templateFieldData.size', 'Size', 'size', true),
      createColumn('templateFieldData.group', 'Group', 'group', true),
      createColumn(
        'templateFieldData.corrosion_category',
        'Corrosion Category',
        'corrosion_category',
        true
      ),
      createColumn(
        'templateFieldData.recommendation_alarm_level',
        'Alarm Level',
        'recommendation_alarm_level',
        true
      ),
    ];
  }, []);

  // Get unique assembly groups for tabs with counts
  const assemblyGroups = useMemo((): string[] => {
    if (!assemblies) return ['All'];
    const groups = assemblies
      .map((assembly) => assembly.templateFieldData?.group)
      .filter((group): group is string => Boolean(group && group !== 'N/A'));
    const uniqueGroups = Array.from(new Set(groups));
    return ['All', ...uniqueGroups];
  }, [assemblies]);

  // Filter assemblies by selected group and search term
  const filteredAssemblies = useMemo(() => {
    if (!assemblies) return [];

    let filtered = assemblies;

    // Filter by group
    if (selectedAssemblyGroup !== 'All') {
      filtered = filtered.filter(
        (assembly) => assembly.templateFieldData?.group === selectedAssemblyGroup
      );
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((assembly) => {
        return assemblyColumns.some((column) => {
          let value: string;

          if (column.templateFieldKey === 'tagName') {
            value = assembly.tagName?.toLowerCase() || '';
          } else {
            const fieldValue = assembly.templateFieldData?.[column.templateFieldKey];
            value = fieldValue?.toString().toLowerCase() || '';
          }

          return value.includes(searchLower);
        });
      });
    }

    return filtered;
  }, [assemblies, selectedAssemblyGroup, searchTerm, assemblyColumns]);

  // Apply client-side pagination to filtered assemblies
  const paginatedAssemblies = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredAssemblies.slice(startIndex, endIndex);
  }, [filteredAssemblies, page, rowsPerPage]);

  const assemblyRows = useMemo(() => {
    return paginatedAssemblies || [];
  }, [paginatedAssemblies]);

  // Update assembly total count based on filtered results
  const filteredAssemblyTotalCount = useMemo(() => {
    return filteredAssemblies.length;
  }, [filteredAssemblies]);

  const handleAssemblyHeaderCheckboxChange = useCallback(() => {
    setSelectedAssemblies((previousSelected) => {
      const newState = { ...previousSelected };

      if (!newState[selectedAssemblyGroup]) {
        newState[selectedAssemblyGroup] = [];
      }

      const currentSelections = newState[selectedAssemblyGroup];
      const currentPageAssemblyIds = assemblyRows.map((assembly) => assembly.id);

      // Check if all current page assemblies are selected
      const allCurrentPageSelected = currentPageAssemblyIds.every((id) =>
        currentSelections.includes(id)
      );

      if (allCurrentPageSelected) {
        // Deselect all current page assemblies
        newState[selectedAssemblyGroup] = currentSelections.filter(
          (id) => !currentPageAssemblyIds.includes(id)
        );
      } else {
        // Select all current page assemblies
        const newSelections = [...currentSelections];
        for (const id of currentPageAssemblyIds) {
          if (!newSelections.includes(id)) {
            newSelections.push(id);
          }
        }
        newState[selectedAssemblyGroup] = newSelections;
      }

      return newState;
    });
  }, [selectedAssemblyGroup, assemblyRows]);

  //should the pois be marked as partially completed???
  const handleMarkComplete = useCallback(async () => {
    setLoading(true);
    // Get all selected assembly IDs
    const selectedAssemblyIds = Object.values(selectedAssemblies).flat();

    // Use the remediation date from props if available, otherwise use current date
    // remediationDate can be an ISO string, Unix timestamp string, or null
    let dateToUse = new Date().toISOString();
    if (remediationDate) {
      // Try to parse as number first (Unix timestamp), otherwise parse as string (ISO date)
      const dateValue = !isNaN(Number(remediationDate)) ? Number(remediationDate) : remediationDate;
      const parsedDate = new Date(dateValue);
      // Check if the date is valid
      if (!isNaN(parsedDate.getTime())) {
        dateToUse = parsedDate.toISOString();
      }
    }
    if (selectedAssemblyIds.length > 0) {
      // Update assemblies status to completed using the correct mutation
      await updateAssemblyCompletion({
        variables: {
          input: {
            structureId: structureId,
            paintRegionId: paintRegionId,
            assemblyIds: selectedAssemblyIds,
            remediatedAt: dateToUse,
            lastModifiedFor: 'Assembly completion status update due to paint region completion',
          },
        },
      });
    }

    const selectedPoiIds = Object.values(selectedPois).flat();
    if (selectedPoiIds.length > 0) {
      await completePointOfInterest({
        variables: {
          input: {
            pointOfInterestIds: selectedPoiIds,
            statusRemediatedThrough: StatusRemediatedThrough.PaintRegion,
            status: PoiStatus.Completed,
            remediationAt: dateToUse,
          },
        },
        onCompleted: () => {
          refetch().then(({ data }) => {
            updatePoiState(data);
          });
        },
        refetchQueries: [PointOfInterestsDocument],
      });
    }
    setLoading(false);
    toggleDialog(false);
    setSelectedPois({});
    setSelectedAssemblies({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedAssemblies,
    selectedPois,
    toggleDialog,
    updateAssemblyCompletion,
    structureId,
    completePointOfInterest,
    refetch,
    updatePoiState,
    remediationDate,
  ]);

  const POITabs = useMemo(() => {
    return (
      <Can I="read" a="PointOfInterest">
        <Stack
          alignItems="center"
          direction="row"
          marginTop={2}
          maxWidth="80%"
          sx={{ ...scrollDesign, pb: 1 }}
        >
          {allPointOfInterestTemplates.map((template) => (
            <>
              {template && (
                <Stack key={template?.id} alignItems="center" direction="row" marginRight={1.5}>
                  <POIDialogTab
                    selected={selectedPOIType === template?.key}
                    key={template.id}
                    onClick={() => {
                      setSelectedPOIType(template?.key);
                    }}
                  >
                    <Typography variant="body2" width="max-content">
                      {template.name} {selectedPois[template.name]?.length ?? 0}
                    </Typography>
                  </POIDialogTab>
                </Stack>
              )}
            </>
          ))}
        </Stack>
      </Can>
    );
  }, [allPointOfInterestTemplates, selectedPOIType, selectedPois]);

  const AssemblyTabs = useMemo(() => {
    return (
      <Stack
        alignItems="center"
        direction="row"
        marginTop={2}
        maxWidth="80%"
        sx={{ ...scrollDesign, pb: 1 }}
      >
        {assemblyGroups.map((group) => {
          const groupCount =
            group === 'All'
              ? (assemblies?.length ?? 0)
              : (assemblies?.filter((assembly) => assembly.templateFieldData?.group === group)
                  .length ?? 0);

          return (
            <Stack key={group} alignItems="center" direction="row" marginRight={1.5}>
              <POIDialogTab
                selected={selectedAssemblyGroup === group}
                onClick={() => {
                  setSelectedAssemblyGroup(group);
                }}
              >
                <Typography variant="body2" width="max-content">
                  {group} ({groupCount})
                </Typography>
              </POIDialogTab>
            </Stack>
          );
        })}
      </Stack>
    );
  }, [assemblyGroups, selectedAssemblyGroup, assemblies]);

  const renderTabsForMainTab = () => {
    if (mainTab === 'poi') {
      return POITabs;
    }
    if (mainTab === 'assembly') {
      return AssemblyTabs;
    }
    return <Box />;
  };

  const handleSort = useCallback(
    (sortByField: string) => {
      const isAsc = sortBy === sortByField && sortDirection === 1;
      setSortDirection(isAsc ? -1 : 1);
      setSortBy(sortByField);
    },
    [setSortDirection, sortBy, sortDirection]
  );

  const renderPoiContent = () => {
    if (poisLoading) {
      return (
        <Box sx={{ minHeight: 400 }}>
          <PageLoader />
        </Box>
      );
    }

    if (pointOfInterests && pointOfInterests.length > 0 && selectedPOIType) {
      return (
        <Box sx={{ height: 500, width: '100%' }}>
          <CustomTable
            columns={poiColumns}
            data={rows ?? []}
            totalCount={poiTotalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            handleSort={handleSort}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onRowSelect={handlePointOfInterestSelect}
            selectedRowIds={selectedPois[selectedPOIType] ?? []}
            onRowClick={handlePoiSelected}
            showGearIcon={false}
            selectedRow={selectedPointOfInterestId}
          />
        </Box>
      );
    }

    return (
      <Typography variant="h5" sx={{ p: 2 }}>
        No pending POIs available for {completedLabel.toLowerCase()}.
      </Typography>
    );
  };

  const renderAssemblyContent = () => {
    if (assembliesLoading) {
      return (
        <Box sx={{ minHeight: 400 }}>
          <PageLoader />
        </Box>
      );
    }

    return (
      <StyledTableContainer>
        <ScrollableBox>
          <StyledTable stickyHeader>
            <CustomTableHead
              orderBy={sortBy}
              order={sortDirection}
              activeColumns={assemblyColumns}
              toggleDialog={() => {}}
              handleHeaderCheckboxChange={handleAssemblyHeaderCheckboxChange}
              handleSort={handleSort}
              showGearIcon={false}
            />

            {assemblyRows.length ? (
              <CustomTableBody
                loading={assembliesLoading}
                data={assemblyRows as Assembly[]}
                activeColumns={assemblyColumns}
                onCheckboxChange={(id: string) => handleAssemblySelect([id])}
                selectedRow={''}
                onRowClick={() => {}}
                selectedIds={selectedAssemblies[selectedAssemblyGroup] ?? []}
              />
            ) : (
              <Typography variant="h5" sx={{ p: 2 }}>
                No pending assemblies available for {completedLabel.toLowerCase()}.
              </Typography>
            )}
          </StyledTable>
        </ScrollableBox>
        <PaginationBox>
          <TablePaginationContainer>
            <TablePagination
              count={filteredAssemblyTotalCount}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPageOptions={[
                { label: '10', value: 10 },
                { label: '20', value: 20 },
                { label: '50', value: 50 },
                { label: '100', value: 100 },
                { label: 'All', value: filteredAssemblyTotalCount },
              ]}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) =>
                setRowsPerPage(Number.parseInt(event.target.value, 10))
              }
              data-testid="assembly-table-pagination"
            />
          </TablePaginationContainer>
        </PaginationBox>
      </StyledTableContainer>
    );
  };

  const renderAreaRiskContent = () => {
    if (areaRiskLoading) {
      return (
        <Box
          sx={{ minHeight: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <StyledTableContainer>
        <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
          <Alert
            severity="info"
            sx={{
              backgroundColor: '#E3F2FD',
              color: abyssColors.primary[700],
              fontWeight: 600,
              '& .MuiAlert-icon': {
                color: abyssColors.primary[500],
              },
              mb: 2,
              mt: 0,
              py: 1.5,
            }}
          >
            The {completedLabel.toLowerCase()} area will be evenly split across Ri0-Ri5. i.e., if
            you enter 100 {areaUnitLabel} (10% of total area), 10% will be applied to each Ri
            category.
          </Alert>

          <Box sx={{ pt: 3, pb: 3 }}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 3 }}>
                <TextField
                  label={`Total area (${areaUnitLabel})`}
                  value={displayTotalArea.toFixed(2)}
                  slotProps={{
                    input: {
                      readOnly: true,
                    },
                  }}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 3 }}>
                <TextField
                  label={`Already ${completedLabel.toLowerCase()} area (${areaUnitLabel})`}
                  value={displayAlreadyCompleted.toFixed(2)}
                  slotProps={{
                    input: {
                      readOnly: true,
                    },
                  }}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 3 }}>
                <TextField
                  label={`${completedLabel} by area percentage`}
                  value={`${completionPercentage.toFixed(2)}%`}
                  slotProps={{
                    input: {
                      readOnly: true,
                    },
                  }}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 3 }}>
                <TextField
                  label={`New ${completedLabel.toLowerCase()} area (${areaUnitLabel})`}
                  type="number"
                  value={totalCompletedArea || ''}
                  onChange={(e) => {
                    const value = Number.parseFloat(e.target.value) || 0;
                    setTotalCompletedArea(value);
                  }}
                  slotProps={{
                    input: {
                      inputProps: {
                        min: 0,
                        step: 0.01,
                      },
                    },
                  }}
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>

            {totalCompletedArea > displayRemainingArea && (
              <Alert
                severity="info"
                sx={{
                  p: 0,
                  backgroundColor: 'transparent',
                  color: abyssColors.primary[700],
                  fontWeight: 400,
                  '& .MuiAlert-icon': {
                    color: abyssColors.warning[600],
                  },
                }}
              >
                The entered {completedLabel.toLowerCase()} area is greater than the available area
                for this paint block. The full value has been saved, but calculations will be capped
                at the available area.
              </Alert>
            )}
          </Box>
        </Box>
      </StyledTableContainer>
    );
  };

  const renderTabContent = () => {
    switch (mainTab) {
      case 'poi':
        return renderPoiContent();
      case 'assembly':
        return renderAssemblyContent();
      case 'areaRisk':
        return renderAreaRiskContent();
      default:
        return null;
    }
  };

  const MainTabs = useMemo(() => {
    return (
      <Tabs
        value={mainTab}
        onChange={(_, newValue) => setMainTab(newValue)}
        sx={{
          borderBottom: `1px solid ${abyssColors.primary[200]}`,
          '& .MuiTabs-indicator': {
            backgroundColor: abyssColors.primary[500],
            height: '0.2rem',
          },
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: '1.4rem',
            fontWeight: 400,
            minWidth: 'fit-content',
            padding: '1.2rem 1.6rem',
            color: abyssColors.primary[400],
            textAlign: 'left',
          },
          '& .MuiTab-root.Mui-selected': {
            color: abyssColors.primary[500],
            fontWeight: 400,
          },
        }}
      >
        <Tab label="Points of interest" value="poi" />
        {deploymentLevel !== StructureDeploymentLevel.FacilityLevel && (
          <Tab label="Components" value="assembly" />
        )}
        {showCoatingAreaTab && <Tab label="Coating area" value="areaRisk" />}
      </Tabs>
    );
  }, [mainTab, deploymentLevel, showCoatingAreaTab]);

  return (
    <Dialog open={isOpen} maxWidth="md" fullWidth sx={{ radius: 2 }}>
      <DialogTitle>
        <Stack direction="column">
          <Typography variant="h6">
            <b>{getDialogTitle(mainTab, completedLabel)}</b>
          </Typography>
          {MainTabs}
        </Stack>
      </DialogTitle>
      <Box display="flex" alignItems="center" justifyContent="space-between" paddingX={3}>
        {renderTabsForMainTab()}
        {mainTab !== 'areaRisk' && (
          <TextField
            data-testid="search-poi-field"
            id="standard-basic"
            label={`${mainTab === 'poi' ? 'Search POI' : 'Search Assembly'}`}
            variant="standard"
            type="search"
            value={unfinishedSearchTerm}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
            onChange={handleSearchTermChanged}
          />
        )}
      </Box>

      <DialogContent sx={{ pt: mainTab === 'areaRisk' ? 1 : undefined }}>
        {renderTabContent()}
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: `1px solid ${abyssColors.primary[200]}`,
        }}
      >
        {mainTab === 'areaRisk' ? (
          <Box sx={{ flex: 1 }} />
        ) : (
          <Stack alignItems="center" direction="row" marginLeft={2}>
            <Typography sx={{ color: abyssColors.primary[400] }}>
              Selected: {getTotalPoiCount} POIs, {getTotalAssemblyCount} Components
            </Typography>
            <Typography fontWeight="700" sx={{ marginLeft: 1 }}>
              (Total: {getTotalSelectedCount})
            </Typography>
          </Stack>
        )}
        <Box>
          <Button
            id="cancel-mark-poi-complete"
            onClick={() => {
              toggleDialog(false);
              setSelectedPois({});
              setSelectedAssemblies({});
              setTotalCompletedArea(0);
            }}
            color="primary"
            sx={{ marginRight: 3 }}
          >
            Cancel
          </Button>
          {mainTab === 'areaRisk' ? (
            <Button
              id="save-area-risk-complete"
              onClick={handleSaveAreaRiskCompletion}
              variant="contained"
              disabled={savingCompletion || totalCompletedArea <= 0}
              sx={{
                maxHeight: '34px',
                backgroundColor: abyssColors.primary[500],
                borderRadius: '4px',
                padding: '8px 12px',
                marginRight: 3,
              }}
            >
              {savingCompletion ? 'Processing...' : 'Save & update'}
            </Button>
          ) : (
            <Button
              id="mark-poi-complete"
              onClick={handleMarkComplete}
              variant="contained"
              disabled={loading}
              sx={{
                maxHeight: '34px',
                backgroundColor: abyssColors.primary[500],
                borderRadius: '4px',
                padding: '8px 12px',
                marginRight: 3,
              }}
            >
              {loading ? 'Processing...' : `Mark as ${completedLabel.toLowerCase()}`}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};
